import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const EXPLORE_SHOW_OPTIONS = [
  { key: "everyone", label: "Everyone" },
  { key: "my_gender", label: "My gender" },
  { key: "men_men", label: "Men interested in men" },
  { key: "women_women", label: "Women interested in women" },
  { key: "men_women", label: "Men interested in women" },
  { key: "women_men", label: "Women interested in men" },
  { key: "multiple", label: "Members interested in multiple genders" },
];
const GENDERS = [["man", "Man"], ["woman", "Woman"], ["nonbinary", "Non-binary"], ["other", "Other"]];
const INTERESTS = [["men", "Men"], ["women", "Women"], ["everyone", "Everyone"]];

// Explore Your Way preferences — editable any time (no logout): gender, who you
// want to connect with, and the discovery filter used across Social Meet.
export default function ExplorePrefsDialog({ open, onOpenChange, onSaved }) {
  const { user, refresh } = useAuth();
  const [gender, setGender] = useState("");
  const [interests, setInterests] = useState([]);
  const [show, setShow] = useState("everyone");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGender(user?.gender || "");
    setInterests(user?.interested_in || []);
    setShow(user?.explore_prefs?.show || "everyone");
  }, [open, user]);

  const toggleInterest = (k) => setInterests((cur) => (k === "everyone" ? (cur.includes("everyone") ? [] : ["everyone"])
    : cur.includes(k) ? cur.filter((x) => x !== k) : [...cur.filter((x) => x !== "everyone"), k]));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/explore-prefs", { gender: gender || null, interested_in: interests, show });
      await refresh();
      window.dispatchEvent(new Event("pandax:refresh-feed"));
      toast.success("Explore preferences saved.");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save preferences."); }
    finally { setBusy(false); }
  };
  const chip = (on) => `px-3 py-2 text-xs border transition-colors ${on ? "border-amber-400 bg-amber-400/15 text-amber-200" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-lg" data-testid="explore-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Compass size={20} className="text-amber-300" /> Explore Your Way</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">You see both sides of My Panda X. Tell us who you'd like to meet — change it whenever you like.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">My gender</div>
            <div className="flex flex-wrap gap-2">{GENDERS.map(([k, l]) => <button key={k} type="button" data-testid={`explore-gender-${k}`} onClick={() => setGender(k)} className={chip(gender === k)}>{l}</button>)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">I want to connect with</div>
            <div className="flex flex-wrap gap-2">{INTERESTS.map(([k, l]) => <button key={k} type="button" data-testid={`explore-interest-${k}`} onClick={() => toggleInterest(k)} className={chip(interests.includes(k))}>{l}</button>)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Show me</div>
            <div className="grid sm:grid-cols-2 gap-2">{EXPLORE_SHOW_OPTIONS.map((o) => <button key={o.key} type="button" data-testid={`explore-show-${o.key}`} onClick={() => setShow(o.key)} className={`${chip(show === o.key)} text-left`}>{o.label}</button>)}</div>
            <p data-testid="explore-show-scope" className="text-[11px] text-zinc-500 mt-2">Applies to Social Meet, Live Now and your Feed. You always see your own posts and Panda announcements.</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" onClick={() => onOpenChange(false)} className="rounded-none bg-white/10 hover:bg-white/15 border border-white/15 text-white">Later</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid="explore-prefs-save" className="rounded-none bg-amber-500 hover:bg-amber-400 text-black font-semibold">{busy ? "Saving…" : "Save preferences"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
